import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "../lib/config";

const NAV = [
  { href: "#home", label: "Home" },
  { href: "#premi", label: "Premi" },
  { href: "#leghe", label: "Leghe" },
  { href: "#regolamento", label: "Regolamento" },
  { href: "#classifica", label: "Classifica" },
  { href: "#fondatori", label: "Fondatori" },
  { href: "/gallery", label: "Gallery", isLink: true },
  { href: "#sponsor", label: "Sponsor" },
  { href: "#trailer", label: "Trailer" },
  { href: "#contatti", label: "Contatti" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const onHome = location.pathname === "/";

  return (
    <motion.nav
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-fp-bg/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="Fanta Partenope"
            className="w-11 h-11 md:w-12 md:h-12 object-contain group-hover:rotate-[-6deg] transition-transform duration-500"
          />
          <div className="leading-none hidden sm:block">
            <div className="font-heading font-black text-base tracking-tight silver-gradient">
              FANTA PARTENOPE
            </div>
            <div className="text-[10px] tracking-[0.3em] text-fp-cyan uppercase mt-1">
              Edizione 2026/2027
            </div>
          </div>
        </Link>

        {onHome && (
          <div className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              n.isLink ? (
                <Link
                  key={n.href}
                  to={n.href}
                  className="text-sm font-medium text-white/80 hover:text-fp-cyan transition-colors relative group"
                  data-testid={`nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-fp-cyan group-hover:w-full transition-all duration-300" />
                </Link>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  className="text-sm font-medium text-white/80 hover:text-fp-cyan transition-colors relative group"
                  data-testid={`nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-fp-cyan group-hover:w-full transition-all duration-300" />
                </a>
              )
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <Link
            to="/register"
            className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full bg-fp-cyan text-fp-bg font-heading font-bold text-sm tracking-wider uppercase hover:shadow-[0_0_25px_rgba(0,229,255,0.6)] hover:-translate-y-0.5 transition-all duration-300"
            data-testid="nav-register-btn"
          >
            Iscriviti →
          </Link>
          {onHome && (
            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-white"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-fp-bg/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                n.isLink ? (
                  <Link
                    key={n.href}
                    to={n.href}
                    onClick={() => setOpen(false)}
                    className="text-white/80 hover:text-fp-cyan font-medium"
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  >
                    {n.label}
                  </Link>
                ) : (
                  <a
                    key={n.href}
                    href={n.href}
                    onClick={() => setOpen(false)}
                    className="text-white/80 hover:text-fp-cyan font-medium"
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  >
                    {n.label}
                  </a>
                )
              ))}
              <Link
                to="/register"
                className="sm:hidden px-5 py-2.5 rounded-full bg-fp-cyan text-fp-bg font-heading font-bold text-sm tracking-wider uppercase text-center"
              >
                Iscriviti →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
